import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as action from '../../../Action/BulkSeriesImport/BulkSeriesImport_Action';
import { errorMessage, formatDateTime } from './helpers';

const ACTIONS = {
    create: ['Created the batch', '#b4b4c4'],
    validate: ['Checked the import', '#b4b4c4'],
    start: ['Started the import', '#00A8F3'],
    retry: ['Retried series', '#ffa45c'],
    retry_episodes: ['Retried episodes', '#ffa45c'],
    cancel: ['Cancelled the batch', '#ff7a6e'],
    publish: ['Published series', '#35ad65'],
};

// One readable line per action, from the audit row's detail JSON.
const describe = (entry) => {
    const d = entry.detail || {};
    switch (entry.action) {
        case 'create':
            return `${d.mode === 'ADD_EPISODES' ? 'Add episodes' : 'New series'} · ${(d.folders || []).length} folder(s): ${(d.folders || []).join(', ')}`;
        case 'start':
            return d.confirmed
                ? `Confirmed ${d.confirmed.seriesCount} series · ${d.confirmed.episodeCount} episodes`
                : `${(d.seriesItemIds || []).length} series queued`;
        case 'retry':
        case 'retry_episodes':
            return `${(d.retried || []).length} re-queued${(d.skipped || []).length ? ` · ${d.skipped.length} skipped` : ''}`;
        case 'cancel':
            return `${d.cancelled ?? 0} item(s) cancelled · ${d.jobsCancelled ?? 0} running transcode(s) stopped`;
        case 'publish':
            return [
                (d.published || []).length ? `Published series ${(d.published || []).map((id) => String(id)).join(', ')}` : null,
                ...(d.skipped || []).map((s) => `Skipped ${s.seriesId}: ${s.reason}`),
            ].filter(Boolean).join('\n');
        default:
            return null;
    }
};

const AuditPanel = ({ batchId, onClose }) => {
    const dispatch = useDispatch();
    const [entries, setEntries] = useState(null);
    const closeRef = useRef(null);

    useEffect(() => {
        closeRef.current?.focus();
        dispatch(action.getBulkImportAudit(batchId))
            .then((res) => setEntries(Array.from(res?.responseDetails || [])))
            .catch((err) => { toast.error(errorMessage(err, 'Failed to load the audit log')); setEntries([]); });
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [dispatch, batchId, onClose]);

    return (
        <>
            <div className="bsi-overlay" onClick={onClose} />
            <aside className="bsi-drawer" role="dialog" aria-modal="true" aria-labelledby="bsi-audit-title">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 14px 14px 22px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <h2 id="bsi-audit-title" style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#fff' }}>Audit log</h2>
                        <span className="bsi-muted" style={{ fontSize: 13 }}>Batch #{batchId} · who did what, and when</span>
                    </div>
                    <button ref={closeRef} type="button" className="bsi-icon-btn" aria-label="Close audit log" onClick={onClose}>
                        <i className="fas fa-times" aria-hidden="true" />
                    </button>
                </div>
                <div style={{ overflowY: 'auto', flexGrow: 1 }}>
                    {entries === null && <div className="bsi-muted" style={{ padding: 22 }}>Loading…</div>}
                    {entries?.length === 0 && <div className="bsi-muted" style={{ padding: 22 }}>No actions recorded yet.</div>}
                    {entries?.length > 0 && (
                        <ol style={{ margin: 0, padding: 22, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 20 }}>
                            {entries.map((e) => {
                                const [label, color] = ACTIONS[e.action] || [e.action, '#b4b4c4'];
                                const text = describe(e);
                                return (
                                    <li key={e.id} style={{ display: 'grid', gridTemplateColumns: '18px minmax(0,1fr)', gap: 12 }}>
                                        <span style={{ width: 11, height: 11, borderRadius: 999, background: color, marginTop: 5, justifySelf: 'center' }} />
                                        <div>
                                            <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{label}</div>
                                            <div className="bsi-muted" style={{ fontSize: 13 }}>{e.admin_name || `Admin #${e.admin_id}`} · {formatDateTime(e.created_at)}</div>
                                            {text && <div style={{ fontSize: 13, lineHeight: 1.5, color: '#b4b4c4', marginTop: 4, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{text}</div>}
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </div>
            </aside>
        </>
    );
};

export default AuditPanel;
